import React, { useState, useEffect } from "react";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import {
  Autocomplete,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { useDebounce } from "../../../helpers";
import { userService } from "../../../services";
import TranslationsEditor from "../../translationsEditor/TranslationsEditor";
import { LocalizationProvider, DateTimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import "dayjs/locale/fr";
import dayjs from "dayjs";
import { Clear } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import {
  addElement,
  updateModalManager,
} from "../../../reducers/configurator/configuratorSlice";
import { useSelector } from "react-redux";
import { resetLayers } from "../../../reducers/configurator/utils";
import preventDoubleTrad from "../../../helpers/prevent-double-trad";

const PREFIX = "GuestDetailsV2";

const classes = {
  root: `${PREFIX}-root`,
  textMargin: `${PREFIX}-textMargin`,
  descriptionField: `${PREFIX}-descriptionField`,
  checkboxesContainer: `${PREFIX}-checkboxesContainer`,
  planifContainer: `${PREFIX}-planifContainer`,
  pickersContainer: `${PREFIX}-pickersContainer`,
  captionError: `${PREFIX}-captionError`,
  presentationContainer: `${PREFIX}-presentationContainer`,
  presentationTitle: `${PREFIX}-presentationTitle`,
  deleteIcon: `${PREFIX}-deleteIcon`,
  translationsContainer: `${PREFIX}-translationsContainer`,
  dividerStyle: `${PREFIX}-dividerStyle`,
  addPlanifButton: `${PREFIX}-addPlanifButton`,
  datePickerStyle: `${PREFIX}-datePickerStyle`,
};

const GuestDetailsContainer = styled("div")(({ theme }) => {
  return {
    [`& .${classes.root}`]: {
      height: "100%",
      width: "100%",
      margin: "0 auto",
      maxWidth: "650px",
    },
    [`& .${classes.textMargin}`]: {
      margin: "1rem 0 !important",
    },
    [`& .${classes.addPlanifButton}`]: {
      fontSize: "0.875rem",
    },
    [`& .${classes.descriptionField}`]: {
      marginBottom: "1rem !important",
    },
    [`& .${classes.datePickerStyle}`]: {
      margin: "16px 0 8px 0",
    },
    [`& .${classes.checkboxesContainer}`]: {
      display: "flex",
      justifyContent: "space-between",
      marginBottom: "1rem",
    },
    [`& .${classes.planifContainer}`]: {
      marginTop: "1rem",
      padding: "0.5rem",
      backgroundColor: "#424242",
      backgroundImage: "none",
    },
    [`& .${classes.pickersContainer}`]: {
      backgroundColor: "#424242",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      padding: "1rem",
      marginBottom: "1rem",
      backgroundImage: "none",
    },
    [`& .${classes.captionError}`]: {
      color: "#F44336",
    },
    [`& .${classes.presentationContainer}`]: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
    [`& .${classes.presentationTitle}`]: {
      color: "rgba(255, 255, 255, 0.7)",
    },
    [`& .${classes.deleteIcon}`]: {
      width: "24px",
      height: "24px",
      color: theme.palette.primary.main,
      marginRight: "0.75rem",
    },
    [`& .${classes.translationsContainer}`]: {
      marginBottom: "1rem !important",
    },
    [`& .${classes.dividerStyle}`]: {
      marginBottom: "1rem !important",
    },
  };
});

const GuestDetails = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const isAdmin = useSelector(
    (state) => state.user?.currentUser?.account?.role
  );
  const selectedTile = useSelector((state) => state.configurator.selectedTile);
  const parentLayout = useSelector((state) => state.configurator.parentLayout);
  const myTile = useSelector((state) =>
    selectedTile
      ? state.configurator.grid[parentLayout][selectedTile.row][
          selectedTile.column
        ]
      : null
  );
  const saveTrigger = useSelector(
    (state) => state.configurator.modalManager.saveTrigger
  );
  const [data, setData] = useState({
    guests: [],
    description: {
      initial: "",
      translations: [],
    },
    planif: [{ from: dayjs(), to: dayjs().add(2, "hour"), error: null }],
    chat: false,
    vocal: false,
  });
  const [fetchedUsers, setFetchedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchUser, setSearchUser] = useState("");
  const debouncedSearchUser = useDebounce(searchUser, 800);

  useEffect(() => {
    if (handleValidData()) dispatch(updateModalManager({ disableSave: true }));
    else dispatch(updateModalManager({ disableSave: false }));
  }, [data.guests, data.planif]);

  useEffect(() => {
    if (saveTrigger) {
      handleSave();
    }
  }, [saveTrigger]);

  useEffect(() => {
    if (myTile.middle.type === "guest") {
      setData({
        ...myTile.middle,
        planif: [...myTile.middle.planif].map((item) => ({
          ...item,
          error: null,
        })),
      });
    }
  }, [myTile]);

  useEffect(() => {
    if (debouncedSearchUser.length > 0) {
      setLoading(true);
      userService
        .getAll(0, 10, {
          login: debouncedSearchUser,
          email: debouncedSearchUser,
        })
        .then((res) => {
          setFetchedUsers(res.result);
          setLoading(false);
        });
    }
  }, [debouncedSearchUser]);

  const handleDateChange = (key, index, value) => {
    let tmp = [...data.planif];
    if (key === "from" && dayjs(value) > dayjs(tmp[index].to)) {
      tmp[index].error = 1;
    } else if (key === "to" && dayjs(value) < dayjs(tmp[index].from)) {
      tmp[index].error = 2;
    } else {
      tmp[index].error = null;
    }
    tmp[index][key] = value;
    setData({ ...data, planif: tmp });
  };

  const handleValidData = () => {
    let isDisabled = false;
    if (
      data.guests.length === 0 ||
      data.planif.length === 0 ||
      data.planif.some((plan) => plan.error)
    ) {
      isDisabled = true;
    }
    return isDisabled;
  };

  const handleSave = () => {
    const tmp = {
      type: "guest",
      guests: [...data.guests],
      vocal: data.vocal,
      chat: data.chat,
      description: data.description,
      layout: parentLayout,
      planif: [...data.planif].map((item) => ({
        from: item.from,
        to: item.to,
      })),
    };
    dispatch(addElement({ layer: "middle", data: tmp, tile: selectedTile }));
    dispatch(updateModalManager(resetLayers.modalManager));
  };

  const handleDeleteTranslation = (transIndex) => {
    let tmp = [...data.description.translations];
    tmp.splice(transIndex, 1);
    setData({
      ...data,
      description: { ...data.description, translations: tmp },
    });
  };

  const handleModifyTranslation = (value, key, transIndex) => {
    let tmp = [...data.description.translations];
    tmp[transIndex] = { ...tmp[transIndex], [key]: value };
    setData({
      ...data,
      description: { ...data.description, translations: tmp },
    });
  };

  const handleAddTranslation = (value, key) => {
    let tmp = { ...data };
    if (key === "translations") {
      const myArr = [...tmp.description[key]];
      const newArr = preventDoubleTrad(myArr, value);
      tmp["description"] = { ...tmp.description, [key]: newArr };
    } else {
      tmp[key] = { ...tmp[key], initial: value };
    }
    setData(tmp);
  };

  const handleMoveTraduction = (fromIndex, toIndex) => {
    if (toIndex >= 0 && toIndex < data.description.translations.length) {
      let tmp = { ...data };
      const myObj = [...tmp.description.translations];
      const tmpElem = myObj[fromIndex];
      myObj.splice(fromIndex, 1);
      myObj.splice(toIndex, 0, tmpElem);
      tmp = {
        ...tmp,
        description: { ...tmp.description, translations: myObj },
      };
      setData(tmp);
    }
  };

  return (
    <GuestDetailsContainer>
      <div className={classes.root}>
        <Typography variant="body1" className={classes.textMargin}>
          {t("configurator.addGuest")}
        </Typography>
        <Autocomplete
          label={t("configurator.guests")}
          id="autocomplete-guests"
          multiple
          freeSolo={true}
          disabled={!isAdmin}
          className={classes.autocomplete}
          options={fetchedUsers ? fetchedUsers.map((user) => user) : []}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          loading={loading}
          getOptionLabel={(option) => {
            return `${option.login} - ${option.email}`;
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("configurator.guests")}
              variant="outlined"
              onChange={(e) => setSearchUser(e.target.value)}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {loading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
            />
          )}
          defaultValue={data.guests}
          value={data.guests}
          onChange={(e, value) => {
            setData({
              ...data,
              guests: value.map((item) => ({
                id: item.id,
                login: item.login,
                email: item.email,
              })),
            });
          }}
        />
        <Typography variant="body1" className={classes.textMargin}>
          {t("configurator.guestPresentationText")}
        </Typography>
        <div className={classes.translationsContainer}>
          <TranslationsEditor
            elements={data.description.translations}
            defaultText={data.description.initial}
            defaultTextKey="description"
            handleDefaultText={handleAddTranslation}
            addTranslation={handleAddTranslation}
            removeTranslation={handleDeleteTranslation}
            handleTranslation={handleModifyTranslation}
            configurator
            translate
            moveTraduction={handleMoveTraduction}
          />
        </div>
        <Divider light className={classes.dividerStyle} />
        <div className={classes.checkboxesContainer}>
          <FormControlLabel
            control={
              <Checkbox
                color={"primary"}
                onChange={(e) => setData({ ...data, vocal: e.target.checked })}
                checked={data.vocal}
              />
            }
            label={
              <Typography variant="body1">
                {t("configurator.guestVocalChat")}
              </Typography>
            }
            name={`vocal-chat`}
          />
          <FormControlLabel
            control={
              <Checkbox
                color={"primary"}
                onChange={(e) => setData({ ...data, chat: e.target.checked })}
                checked={data.chat}
              />
            }
            label={
              <Typography variant="body1">
                {t("configurator.guestChat")}
              </Typography>
            }
            name={`chat`}
          />
        </div>
        <div>
          <Button
            variant="contained"
            color="primary"
            className={classes.addPlanifButton}
            onClick={() => {
              let tmp = [...data.planif];
              tmp.push({
                from: dayjs(),
                to: dayjs().add(2, "hour"),
                error: null,
              });
              setData({ ...data, planif: tmp });
            }}
          >
            {t("configurator.guestsAddPlanif")}
          </Button>
          {data.planif.length > 0 && (
            <Paper elevation={12} className={classes.planifContainer}>
              <Typography variant="body2" className={classes.descriptionField}>
                {t("configurator.guestSelectedTime")}
              </Typography>
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale={i18next.language.substring(0, 2)}
              >
                {data.planif.map((item, index) => {
                  return (
                    <Paper
                      elevation={12}
                      className={classes.pickersContainer}
                      key={`picker-container-${index + 1}`}
                    >
                      <div className={classes.presentationContainer}>
                        <Typography
                          variant="body2"
                          className={classes.presentationTitle}
                        >
                          {t("configurator.guestPresentationTitle")}
                          {index + 1}
                        </Typography>
                        <IconButton
                          className={classes.deleteIcon}
                          onClick={() => {
                            const tmp = [...data.planif];
                            tmp.splice(tmp[index], 1);
                            setData({ ...data, planif: tmp });
                          }}
                        >
                          <Clear />
                        </IconButton>
                      </div>
                      <DateTimePicker
                        id="from-date-picker-dialog"
                        label={t("myDesign.zones.schedule.from")}
                        className={classes.datePickerStyle}
                        value={dayjs(item.from)}
                        onChange={(e) => handleDateChange("from", index, e)}
                        KeyboardButtonProps={{
                          "aria-label": "change date",
                        }}
                        error={item.error === 1}
                      />
                      {item.error === 1 && (
                        <Typography
                          variant="caption"
                          className={classes.captionError}
                        >
                          {t("configurator.guestPickerErrorFrom")}
                        </Typography>
                      )}
                      <DateTimePicker
                        id="to-date-picker-dialog"
                        label={t("myDesign.zones.schedule.to")}
                        className={classes.datePickerStyle}
                        value={dayjs(item.to)}
                        onChange={(e) => handleDateChange("to", index, e)}
                        KeyboardButtonProps={{
                          "aria-label": "change date",
                        }}
                        minDate={dayjs(item.from)}
                        error={item.error === 2}
                      />
                      {item.error === 2 && (
                        <Typography
                          variant="caption"
                          className={classes.captionError}
                        >
                          {t("configurator.guestPickerErrorTo")}
                        </Typography>
                      )}
                    </Paper>
                  );
                })}
              </LocalizationProvider>
            </Paper>
          )}
        </div>
      </div>
    </GuestDetailsContainer>
  );
};

export default GuestDetails;
